import React, { useState } from 'react';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';

import { routePathFavorites } from 'src/constants/router';
import { colours } from 'src/constants/colours';

import useAppRouter from 'src/hooks/useAppRouter';
import useLoginModal from 'src/hooks/useLoginModal';

import type { Medium } from 'src/types/__generated__/graphql';

import { styledComponent, styledTag } from 'src/utils/styled';
import type { GalleryCollaborator } from 'src/components/favorites/CollaboratorsAvatarList';
import { CollaboratorsAvatarList } from 'src/components/favorites/CollaboratorsAvatarList';
import Tooltip from 'src/components/ui/Tooltip';
import { GalleryPreviewDataInfos } from 'src/components/favorites/GalleryPreview/view/GalleryPreviewDataInfos';
import { GalleryPreviewImage } from 'src/components/favorites/GalleryPreview/view/GalleryPreviewImage';
import Button from 'src/components/ui/Button';

const TinyCardMediaContainer = styledTag('div')<{ $size: string; $margin: string; $backgroundColor: string }>`
    ${({ $size }) => `height: ${$size};`}
    ${({ $size }) => `width: ${$size};`}
    ${({ $margin }) => `margin: ${$margin};`}
    ${({ $backgroundColor }) => `background-color: ${$backgroundColor};`}
    border-radius: 0.25rem;
    overflow: hidden;
    img,
    video {
        height: 100%;
        width: 100%;
        border-radius: 0.25rem;
    }
`;

const StyledTinyCard = styledComponent(Box)<{ $isCursorPointer: boolean; $changeBackgroundOnHover: boolean }>`
    display: flex;
    align-items: center;
    width: 100%;
    border-radius: 0.25rem;
    ${({ $isCursorPointer }) => $isCursorPointer && 'cursor: pointer;'}
    video {
        pointer-events: none;
    }
    ${({ $changeBackgroundOnHover }) =>
        $changeBackgroundOnHover &&
        `
        &:hover {
            background-color: ${colours.secondary.O08};
        }
    `}
`;

const SecretIconWrapper = styledComponent(Box)<{ $showCollaborators: boolean }>`
    margin-left: 1rem;
    margin-right: ${({ $showCollaborators }) => ($showCollaborators ? '0' : '1rem')};
`;

type PreviewMedia = Array<{
    type: Medium['type'];
    url: Medium['url'];
    isTranscoding?: Medium['isTranscoding'];
} | null>;

type GalleryData = {
    id: string;
    name: string;
    numberOfContents: number;
    previewMedia?: PreviewMedia | null;
    isSecret: boolean;
    contentLastUpdatedAt?: Date;
    collaborators?: {
        items: GalleryCollaborator[];
        totalCount: number;
    };
};

interface Props {
    gallery: GalleryData;
    minimalist?: boolean;
    showGalleryDataInfos?: boolean;
    showCollaborators?: boolean;
    redirectOnClick?: boolean;
    showSelectButtonOnHover?: boolean;
    onSelectClick?: () => void;
    changeBackgroundOnHover?: boolean;
}

export const GalleryPreviewRow: React.FC<Props> = ({
    gallery,
    minimalist = false,
    showGalleryDataInfos = true,
    showCollaborators = true,
    redirectOnClick = true,
    showSelectButtonOnHover,
    onSelectClick,
    changeBackgroundOnHover = false,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useTranslation(['favorites']);
    const router = useAppRouter();
    const { createAuthenticatedHandler } = useLoginModal();

    const handleCardClick = async () => {
        await createAuthenticatedHandler(async () => {
            await router.push(`${routePathFavorites}/${gallery.id}`);
        });
    };

    const showSecretIcon = gallery.isSecret && !minimalist;
    const showSelectButton = showSelectButtonOnHover && isHovered;
    return (
        <StyledTinyCard
            $isCursorPointer={minimalist}
            $changeBackgroundOnHover={changeBackgroundOnHover}
            onClick={redirectOnClick ? handleCardClick : undefined}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <TinyCardMediaContainer
                $size={minimalist ? '2.5rem' : '3rem'}
                $margin={minimalist ? '1rem 1rem 1rem 0' : '0.5rem 0.75rem'}
                $backgroundColor={minimalist ? colours.black.O16 : colours.secondary.O16}
            >
                <GalleryPreviewImage previewMedia={gallery.previewMedia} showShowcaseGrid={false} />
            </TinyCardMediaContainer>
            <Box flex={1} minWidth={0} display="flex" justifyContent="space-between" alignItems="center">
                <Box flex={1} minWidth={0}>
                    <Typography
                        data-target-id={`gallery-card-title-id-${gallery.id}`}
                        variant={minimalist ? 'h6' : 'h5'}
                        style={{ color: colours.black.main }}
                        noWrap
                    >
                        {gallery.name}
                    </Typography>
                    {showGalleryDataInfos && (
                        <GalleryPreviewDataInfos
                            gallery={gallery}
                            useCaptionVariant={minimalist}
                            dateFontSize="0.75rem"
                            gapBetweenSubtitleAndDate="0.5rem"
                        />
                    )}
                </Box>
                {!showSelectButton && showSecretIcon && (
                    <SecretIconWrapper $showCollaborators={showCollaborators}>
                        <Tooltip title={t('favorites:privateGallery')}>
                            <Icon name="Lock" size="1.85rem" color={colours.black.main} />
                        </Tooltip>
                    </SecretIconWrapper>
                )}
                {!showSelectButton && showCollaborators && gallery.collaborators?.items.length && (
                    <Box mx="1rem">
                        <CollaboratorsAvatarList
                            collaborators={gallery.collaborators.items}
                            additionalCollaboratorsCount={
                                gallery.collaborators.totalCount - gallery.collaborators.items.length
                            }
                            avatarSize={26}
                            avatarOverlapMargin="0.65rem"
                        />
                    </Box>
                )}
                {showSelectButton && (
                    <Box mx="1rem">
                        <Button onClick={onSelectClick}>{t('common:select')}</Button>
                    </Box>
                )}
            </Box>
        </StyledTinyCard>
    );
};
